import React, { useEffect, useMemo } from 'react';
import { GeoJSON, CircleMarker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import NexusMap from '../maps/NexusMap';
import type { EvidenceByCountry, MemberNation } from './types';
import { MEL_MEMBER_FILL, MEL_NEIGHBOUR_FILL } from './types';

const BUBBLE_FILL = '#eab308';
const BUBBLE_STROKE = '#a16207';
const BUBBLE_RADIUS = 4;
const ACTIVITY_FILL = '#15736d';
const MUTED_FILL = '#e8eeec';

export const EUROPE_MAP_CENTER: [number, number] = [40, 26];
export const EUROPE_DEFAULT_ZOOM = 4.2;

function FitEuropeFrame({ redrawToken }: { redrawToken?: string | number }) {
  const map = useMap();
  useEffect(() => {
    const t = window.setTimeout(() => {
      map.invalidateSize();
      const bounds = L.latLngBounds([28, -12], [58, 48]);
      map.fitBounds(bounds, { padding: [24, 24], maxZoom: 5, animate: false });
    }, 80);
    return () => window.clearTimeout(t);
  }, [map, redrawToken]);
  return null;
}

function featureIso3(feature: GeoJSON.Feature): string | null {
  const p = (feature.properties || {}) as Record<string, unknown>;
  const raw =
    p.ISO3CD ??
    p.iso3cd ??
    p.iso3 ??
    p.ISO_A3 ??
    p.ADM0_A3 ??
    null;
  return raw != null && String(raw).trim() !== '' ? String(raw).trim().toUpperCase() : null;
}

function featureName(feature: GeoJSON.Feature): string | null {
  const p = (feature.properties || {}) as Record<string, unknown>;
  const raw = p.ROMNAM ?? p.romnam ?? p.name ?? null;
  return raw != null ? String(raw) : null;
}

function isFlagOn(val: unknown): boolean {
  return val === 1 || val === true || val === '1';
}

function formatActivityTypes(types?: Record<string, number>): string {
  if (!types || !Object.keys(types).length) return '';
  return Object.entries(types)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
    .join(' · ');
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildHoverHtml(
  name: string,
  roleLabel: string,
  ev: EvidenceByCountry | undefined,
  activityFilterActive: boolean
): string {
  let html = `<strong>${escapeHtml(name)}</strong>`;
  if (roleLabel) html += `<br/><span style="color:#4b5563;font-size:11px">${escapeHtml(roleLabel)}</span>`;

  if (!ev || !ev.activity_count) {
    if (activityFilterActive) {
      html += '<br/><span style="color:#9ca3af">No activities in this key area</span>';
    }
    return html;
  }

  const snippets = (ev.activities || []).slice(0, 5);
  if (snippets.length) {
    html += '<br/><ul style="margin:6px 0 0;padding-left:14px;font-size:11px;line-height:1.35;color:#1f2937">';
    for (const a of snippets) {
      const bits: string[] = [];
      if (a.date) bits.push(String(a.date));
      if (a.participants) bits.push(`${a.participants} pax`);
      if (a.activity_type) bits.push(a.activity_type.replace(/_/g, ' '));
      const meta = bits.length
        ? ` <span style="color:#6b7280">(${escapeHtml(bits.join(' · '))})</span>`
        : '';
      html += `<li style="margin:0 0 3px"><span style="font-weight:600">${escapeHtml(
        a.title
      )}</span>${meta}</li>`;
    }
    html += '</ul>';
    if (ev.activity_count > snippets.length) {
      html += `<span style="color:#6b7280;font-size:11px">+${
        ev.activity_count - snippets.length
      } more</span>`;
    }
  } else {
    html += `<br/><span style="color:#0f5b57;font-weight:600">${ev.activity_count} activit${
      ev.activity_count === 1 ? 'y' : 'ies'
    }</span>`;
    const typeLine = formatActivityTypes(ev.activity_types);
    if (typeLine) {
      html += `<br/><span style="color:#4b5563;font-size:11px">${escapeHtml(typeLine)}</span>`;
    }
  }

  if (ev.participants_sum > 0) {
    html += `<br/><span style="color:#4b5563;font-size:11px">${ev.participants_sum} participant${
      ev.participants_sum === 1 ? '' : 's'
    } total</span>`;
  }

  return html;
}

interface ImpactMapProps {
  geojson: GeoJSON.FeatureCollection | null;
  membership: MemberNation[];
  evidenceByCountry: EvidenceByCountry[];
  selectedIso3: string | null;
  /** When true, only countries with activities for the active key area are coloured. */
  activityFilterActive?: boolean;
  layoutToken?: string | number;
  onSelectCountry: (iso3: string, name?: string) => void;
  height?: string;
}

const ImpactMap: React.FC<ImpactMapProps> = ({
  geojson,
  membership,
  evidenceByCountry,
  selectedIso3,
  activityFilterActive = false,
  layoutToken,
  onSelectCountry,
  height = '520px',
}) => {
  const memberMap = useMemo(() => {
    const m = new Map<string, MemberNation>();
    membership.forEach((n) => m.set(String(n.iso3).toUpperCase(), n));
    return m;
  }, [membership]);

  const evidenceMap = useMemo(() => {
    const m = new Map<string, EvidenceByCountry>();
    evidenceByCountry.forEach((e) => m.set(String(e.iso3).toUpperCase(), e));
    return m;
  }, [evidenceByCountry]);

  const maxActivity = useMemo(() => {
    let max = 1;
    evidenceByCountry.forEach((e) => {
      if (e.activity_count > max) max = e.activity_count;
    });
    return max;
  }, [evidenceByCountry]);

  const centroids = useMemo(() => {
    if (!geojson) return [] as { iso3: string; lat: number; lng: number; count: number; name: string }[];
    const out: { iso3: string; lat: number; lng: number; count: number; name: string }[] = [];
    for (const f of geojson.features) {
      const iso3 = featureIso3(f);
      if (!iso3) continue;
      const ev = evidenceMap.get(iso3);
      if (!ev || !ev.activity_count) continue;
      try {
        const layer = L.geoJSON(f as any);
        const b = layer.getBounds();
        if (!b.isValid()) continue;
        const c = b.getCenter();
        out.push({
          iso3,
          lat: c.lat,
          lng: c.lng,
          count: ev.activity_count,
          name: ev.country_name || memberMap.get(iso3)?.name || iso3,
        });
      } catch {
        /* skip */
      }
    }
    return out;
  }, [geojson, evidenceMap, memberMap]);

  const styleFn = (feature?: GeoJSON.Feature) => {
    if (!feature) return {};
    const iso3 = featureIso3(feature);
    const member = iso3 ? memberMap.get(iso3) : undefined;
    const ev = iso3 ? evidenceMap.get(iso3) : undefined;
    const hasActivity = !!(ev && ev.activity_count > 0);
    const isMember = !!(member && isFlagOn(member.is_member));
    const isNeighbour = !!(member && isFlagOn(member.is_neighbour));
    const selected = iso3 && selectedIso3 === iso3;

    let fill = MUTED_FILL;
    let fillOpacity = 0.22;
    let weight = 0.8;
    let color = '#ffffff';

    if (activityFilterActive) {
      // Key-area mode: only paint countries with activities for this key area
      if (hasActivity) {
        const intensity = Math.min(1, (ev!.activity_count || 1) / maxActivity);
        fill = ACTIVITY_FILL;
        fillOpacity = 0.45 + intensity * 0.4;
        weight = 1.4;
        color = '#0f5b57';
      } else if (isMember || isNeighbour) {
        fill = MUTED_FILL;
        fillOpacity = 0.18;
        weight = 0.7;
        color = '#d1d5db';
      }
    } else {
      // Overview: members + neighbours choropleth
      if (isMember) {
        fill = MEL_MEMBER_FILL;
        fillOpacity = 0.78;
        weight = 1.2;
      } else if (isNeighbour) {
        fill = MEL_NEIGHBOUR_FILL;
        fillOpacity = 0.62;
        weight = 1;
      }
    }

    if (selected) {
      fillOpacity = Math.min(0.95, fillOpacity + 0.15);
      weight = 2.5;
      color = '#083d39';
    }

    return {
      fillColor: fill,
      weight,
      opacity: 1,
      color,
      fillOpacity,
      className: 'mel-country-path',
    };
  };

  const geoKey = useMemo(
    () =>
      `${activityFilterActive ? 'f' : 'o'}-${geojson?.features?.length || 0}-m${
        membership.length
      }-${selectedIso3 || ''}-${evidenceByCountry.map((e) => `${e.iso3}:${e.activity_count}`).join('|')}`,
    [activityFilterActive, geojson, membership.length, selectedIso3, evidenceByCountry]
  );

  const onEachFeature = (feature: GeoJSON.Feature, layer: L.Layer) => {
    const iso3 = featureIso3(feature);
    const member = iso3 ? memberMap.get(iso3) : undefined;
    const name =
      member?.name ||
      featureName(feature) ||
      iso3 ||
      'Country';
    const ev = iso3 ? evidenceMap.get(iso3) : undefined;

    const isMember = !!(member && isFlagOn(member.is_member));
    const isNeighbour = !!(member && isFlagOn(member.is_neighbour));
    const roleLabel = isMember
      ? 'EuFMD member nation'
      : isNeighbour
        ? 'Neighbouring country'
        : '';

    layer.bindTooltip(buildHoverHtml(name, roleLabel, ev, activityFilterActive), {
      sticky: true,
      opacity: 0.95,
      className: 'mel-map-tooltip',
    });

    layer.on({
      mouseover: (e) => {
        const path = e.target as L.Path;
        path.setStyle({ weight: 2.5, color: '#0f5b57' });
        path.bringToFront();
      },
      mouseout: (e) => {
        const path = e.target as L.Path;
        const s = styleFn(feature) as L.PathOptions;
        path.setStyle(s);
      },
      click: (e) => {
        if (!iso3) return;
        const path = e.target as L.Path;
        const bounds = (path as any).getBounds?.();
        if (bounds?.isValid?.()) {
          const map = (path as any)._map as L.Map | undefined;
          map?.flyToBounds(bounds, { maxZoom: 5.5, padding: [60, 60], duration: 0.45 });
        }
        onSelectCountry(iso3, name);
      },
    });
  };

  return (
    <div className="w-full" style={{ height: 520, minHeight: 520 }}>
      <NexusMap
        center={EUROPE_MAP_CENTER}
        zoom={EUROPE_DEFAULT_ZOOM}
        minZoom={3}
        maxZoom={8}
        height="520px"
        className="shadow-sm h-full"
        mapClassName="h-full w-full rounded-t-lg"
        disclaimerClassName="px-3 py-2 bg-gray-50 border-t border-gray-200"
        disclaimerTextClassName="text-xs sm:text-sm text-gray-600 leading-snug"
      >
        <FitEuropeFrame
          redrawToken={`${layoutToken || ''}-${geojson?.features?.length || 0}-${activityFilterActive}`}
        />
        {geojson && (
          <GeoJSON
            key={geoKey}
            data={geojson as any}
            style={styleFn as any}
            onEachFeature={onEachFeature}
          />
        )}
        {centroids.map((c) => {
          const ev = evidenceMap.get(c.iso3);
          const snippets = (ev?.activities || []).slice(0, 4);
          return (
            <CircleMarker
              key={`bubble-${c.iso3}`}
              center={[c.lat, c.lng]}
              radius={BUBBLE_RADIUS}
              pathOptions={{
                color: BUBBLE_STROKE,
                weight: 1,
                fillColor: BUBBLE_FILL,
                fillOpacity: 0.95,
              }}
              eventHandlers={{
                click: () => onSelectCountry(c.iso3, c.name),
              }}
            >
              <Popup>
                <strong>{c.name}</strong>
                {snippets.length > 0 ? (
                  <ul style={{ margin: '6px 0 0', paddingLeft: 16, fontSize: 12 }}>
                    {snippets.map((a, i) => (
                      <li key={`${c.iso3}-${i}`}>
                        {a.title}
                        {a.date || a.participants
                          ? ` (${[a.date, a.participants ? `${a.participants} pax` : null]
                              .filter(Boolean)
                              .join(' · ')})`
                          : ''}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <>
                    <br />
                    {c.count} recorded activit{c.count === 1 ? 'y' : 'ies'}
                  </>
                )}
              </Popup>
            </CircleMarker>
          );
        })}
      </NexusMap>
    </div>
  );
};

export default ImpactMap;
